"use client";

import { useMemo } from "react";
import {
  applyListingFilters,
  buildFilterOptions,
  countActiveFilters,
  isSearching,
  LOCATION_PAGE_SIZE,
  type BrowserListing,
  type ListingFilterOptions,
  type ListingFilters,
} from "./listing-filters";
import { useListingBrowserState } from "./use-listing-browser-state";
import { useLocationListings } from "./use-location-listings";

export type LocationListingSource = {
  firstPage: BrowserListing[];
  total: number;
  options: ListingFilterOptions;
  listingsHref: string;
};

function isFirstPageView(filters: ListingFilters, page: number): boolean {
  return (
    page <= 1 && filters.sort === "default" && countActiveFilters(filters) === 0 && !isSearching(filters)
  );
}

function pageOf(results: BrowserListing[], page: number) {
  const pageCount = Math.max(1, Math.ceil(results.length / LOCATION_PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const start = (currentPage - 1) * LOCATION_PAGE_SIZE;
  return { pageCount, currentPage, pageCards: results.slice(start, start + LOCATION_PAGE_SIZE) };
}

export function useListingResults({ firstPage, total: serverTotal, options: serverOptions, listingsHref }: LocationListingSource) {
  const { filters, page, setFilters, setPage } = useListingBrowserState();
  const isComplete = firstPage.length >= serverTotal;
  const needsAll = !isComplete && !isFirstPageView(filters, page);
  const listings = useLocationListings(isComplete ? null : listingsHref, needsAll);
  const all = listings.all ?? (isComplete ? firstPage : null);
  const results = useMemo(
    () => (all ? applyListingFilters(all, filters) : null),
    [all, filters],
  );
  const options = useMemo(
    () => (listings.all ? buildFilterOptions(listings.all) : serverOptions),
    [listings.all, serverOptions],
  );
  const total = listings.all?.length ?? serverTotal;
  const view = results
    ? { ...pageOf(results, page), resultCount: results.length }
    : {
        pageCount: Math.max(1, Math.ceil(total / LOCATION_PAGE_SIZE)),
        currentPage: needsAll ? page : 1,
        pageCards: firstPage,
        resultCount: total,
      };
  return {
    filters,
    setFilters,
    setPage,
    ...view,
    total,
    options,
    all,
    isLoading: needsAll && listings.all === null && !listings.failed,
    failed: needsAll && listings.failed,
    listingsFailed: listings.failed,
    requestAll: listings.request,
  };
}

export type ListingResults = ReturnType<typeof useListingResults>;
